import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const YEARS_PER_PAGE = 12;

// value / onChange use "YYYY-MM" (first-of-month semantics), so this
// drops straight into anything already storing attendance filters as
// a plain month string. Swap parse/format below if you'd rather keep
// a Date object internally.
const parseYM = (value) => {
  if (!value) return null;
  const [y, m] = value.split("-").map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1);
};

const toYM = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const formatDisplay = (date) =>
  `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;

export default function MonthYearPicker({
  value,
  onChange,
  placeholder = "Select month",
  minDate,   // "YYYY-MM" or "YYYY-MM-DD", optional
  maxDate,
  className = "",
  disabled = false,
}) {
  const selected = parseYM(value);
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(selected ? selected.getFullYear() : new Date().getFullYear());
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  // "months" | "years" — clicking the year in the header drills into a
  // 12-year grid, same pattern as the day-level DatePicker.
  const [view, setView] = useState("months");

  const wrapperRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    setCoords({ top: rect.bottom + window.scrollY + 6, left: rect.left + window.scrollX });
  }, []);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  const handleToggle = () => {
    if (!open) {
      updatePosition();
      setViewYear(selected ? selected.getFullYear() : new Date().getFullYear());
      setView("months");
    }
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      const clickedBtn = wrapperRef.current?.contains(e.target);
      const clickedPanel = panelRef.current?.contains(e.target);
      if (!clickedBtn && !clickedPanel) setOpen(false);
    };
    const handleEscape = (e) => { if (e.key === "Escape") setOpen(false); };

    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  const min = minDate ? parseYM(minDate.slice(0, 7)) : null;
  const max = maxDate ? parseYM(maxDate.slice(0, 7)) : null;

  // ---- navigation (prev/next behaves differently per view) ----
  const goPrev = () => {
    if (view === "months") setViewYear((y) => y - 1);
    else setViewYear((y) => y - YEARS_PER_PAGE);
  };
  const goNext = () => {
    if (view === "months") setViewYear((y) => y + 1);
    else setViewYear((y) => y + YEARS_PER_PAGE);
  };

  // ---- months grid (12 months of viewYear) ----
  const isMonthDisabled = (m) => {
    const monthDate = new Date(viewYear, m, 1);
    if (min && monthDate < min) return true;
    if (max && monthDate > max) return true;
    return false;
  };

  const isMonthSelected = (m) =>
    selected && selected.getFullYear() === viewYear && selected.getMonth() === m;

  const pickMonth = (m) => {
    if (isMonthDisabled(m)) return;
    onChange(toYM(new Date(viewYear, m, 1)));
    setOpen(false);
  };

  // ---- years grid (page of YEARS_PER_PAGE years) ----
  const pageStart = viewYear - (((viewYear % YEARS_PER_PAGE) + YEARS_PER_PAGE) % YEARS_PER_PAGE);
  const yearCells = Array.from({ length: YEARS_PER_PAGE }, (_, i) => pageStart + i);

  const isYearDisabled = (y) => {
    const yearStart = new Date(y, 0, 1);
    const yearEnd = new Date(y, 11, 31);
    if (min && yearEnd < min) return true;
    if (max && yearStart > max) return true;
    return false;
  };

  const pickYear = (y) => {
    if (isYearDisabled(y)) return;
    setViewYear(y);
    setView("months");
  };

  const clear = () => {
    onChange(null);
    setOpen(false);
  };

  const goThisMonth = () => {
    const t = new Date();
    if ((min && new Date(t.getFullYear(), t.getMonth(), 1) < min) ||
        (max && new Date(t.getFullYear(), t.getMonth(), 1) > max)) return;
    setViewYear(t.getFullYear());
    setView("months");
    onChange(toYM(t));
    setOpen(false);
  };

  const headerLabel =
    view === "months" ? (
      <span className="date-picker-title-part" onClick={() => setView("years")}>
        {viewYear}
      </span>
    ) : (
      `${pageStart} - ${pageStart + YEARS_PER_PAGE - 1}`
    );

  return (
    <div className={`date-picker-wrap ${className}`} ref={wrapperRef}>
      <button
        type="button"
        ref={btnRef}
        className="date-picker-btn"
        disabled={disabled}
        onClick={handleToggle}
      >
        <FaCalendarAlt className="date-picker-icon" />
        {selected ? <span>{formatDisplay(selected)}</span> : <span className="date-picker-placeholder">{placeholder}</span>}
      </button>

      {open &&
        createPortal(
          <div
            className="date-picker-panel"
            ref={panelRef}
            style={{ position: "absolute", top: coords.top, left: coords.left }}
          >
            <div className="date-picker-header">
              <button type="button" className="date-picker-nav-btn" onClick={goPrev}>
                <FaChevronLeft />
              </button>
              <span className="date-picker-title">{headerLabel}</span>
              <button type="button" className="date-picker-nav-btn" onClick={goNext}>
                <FaChevronRight />
              </button>
            </div>

            {view === "months" && (
              <>
                <div className="date-picker-months-grid">
                  {MONTH_SHORT.map((m, i) => (
                    <span
                      key={m}
                      className={`date-picker-month-cell ${isMonthSelected(i) ? "selected" : ""} ${isMonthDisabled(i) ? "disabled" : ""}`}
                      onClick={() => pickMonth(i)}
                    >
                      {m}
                    </span>
                  ))}
                </div>

                <div className="date-picker-footer" style={{ justifyContent: "space-between" }}>
                  <button type="button" className="date-picker-today-btn" onClick={clear}>
                    Clear
                  </button>
                  <button type="button" className="date-picker-today-btn" onClick={goThisMonth}>
                    This month
                  </button>
                </div>
              </>
            )}

            {view === "years" && (
              <div className="date-picker-years-grid">
                {yearCells.map((y) => (
                  <span
                    key={y}
                    className={`date-picker-year-cell ${y === viewYear ? "selected" : ""} ${isYearDisabled(y) ? "disabled" : ""}`}
                    onClick={() => pickYear(y)}
                  >
                    {y}
                  </span>
                ))}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}